import { z } from "zod";

const videoAspectSchema = z.enum(["portrait", "landscape", "square"]);
const videoProjectFormatSchema = z.enum([
	"story",
	"documentary",
	"news",
	"advertisement",
]);

type CatalogVideoAspect = z.infer<typeof videoAspectSchema>;
type CatalogVideoProjectFormat = z.infer<typeof videoProjectFormatSchema>;

/** How a video entered the org channel catalog. */
export const ChannelPublishViaSchema = z.enum(["social", "manual"]);
export type ChannelPublishVia = z.infer<typeof ChannelPublishViaSchema>;

/** Catalog index metadata stored on VideoProject.publishTag (social publish). */
export const ChannelPublishTagSchema = z.object({
	type: z.literal("channel_video"),
	aspect: videoAspectSchema,
	durationMs: z.number().int().nonnegative(),
	format: videoProjectFormatSchema,
	publishedVia: ChannelPublishViaSchema,
	socialPostId: z.string().min(1).optional(),
	channelName: z.string().min(1).optional(),
	narratorProfileId: z.string().min(1).optional(),
});
export type ChannelPublishTag = z.infer<typeof ChannelPublishTagSchema>;

/** Legacy manual story episode tag (kept for backward compatibility). */
export const StoryPublishTagSchema = z.object({
	type: z.literal("story"),
	episodeNumber: z.number().int().positive(),
});
export type StoryPublishTag = z.infer<typeof StoryPublishTagSchema>;

export const VideoProjectPublishTagSchema = z.discriminatedUnion("type", [
	StoryPublishTagSchema,
	ChannelPublishTagSchema,
]);
export type VideoProjectPublishTag = z.infer<typeof VideoProjectPublishTagSchema>;

export interface ChannelCatalogEntry {
	videoProjectId: string;
	title: string;
	/** Short hook for LLM context (not the full brief). */
	summary: string;
	durationMs: number;
	aspect: CatalogVideoAspect;
	format: CatalogVideoProjectFormat;
	publishedAt: string;
}

export const CHANNEL_CATALOG_MAX_ENTRIES = 20;
export const CHANNEL_CATALOG_SUMMARY_MAX_CHARS = 120;

export function parseStoryPublishTag(raw: unknown): StoryPublishTag | null {
	const parsed = StoryPublishTagSchema.safeParse(raw);
	return parsed.success ? parsed.data : null;
}

export function parseChannelPublishTag(raw: unknown): ChannelPublishTag | null {
	const parsed = ChannelPublishTagSchema.safeParse(raw);
	return parsed.success ? parsed.data : null;
}

export function parseVideoProjectPublishTag(
	raw: unknown,
): VideoProjectPublishTag | null {
	const parsed = VideoProjectPublishTagSchema.safeParse(raw);
	return parsed.success ? parsed.data : null;
}

/** Short catalog description for prior-video context (title + optional logline). */
export function buildChannelCatalogSummary(input: {
	title: string;
	logline?: string | null;
	maxChars?: number;
}): string {
	const maxChars = input.maxChars ?? CHANNEL_CATALOG_SUMMARY_MAX_CHARS;
	const title = input.title.trim();
	const logline = input.logline?.trim() ?? "";
	if (!title) return "";
	const summary =
		logline && logline !== title ? `${title}. ${logline}` : title;
	return summary.slice(0, maxChars);
}

export function formatChannelCatalogForLlm(
	entries: ChannelCatalogEntry[],
	options?: { channelName?: string | null },
): string {
	if (entries.length === 0) return "";
	const channelName = options?.channelName?.trim();
	const lines = [
		"## Prior published videos (same organization; optional callbacks only)",
		channelName
			? `Channel: ${channelName}. Reference a prior video only when it fits naturally — never forced.`
			: "Reference a prior video only when it fits naturally — never forced.",
		JSON.stringify(
			entries.map((entry) => ({
				title: entry.title,
				summary: entry.summary,
				durationMs: entry.durationMs,
				aspect: entry.aspect,
				format: entry.format,
				publishedAt: entry.publishedAt,
			})),
			null,
			2,
		),
	];
	return lines.join("\n");
}

export function isLandscapeChannelMode(input: {
	format?: CatalogVideoProjectFormat | string | null;
	aspect?: CatalogVideoAspect | string | null;
}): boolean {
	return input.format === "story" && input.aspect === "landscape";
}

/** Map a published channel_video row to a catalog entry (null if not indexed). */
export function projectToChannelCatalogEntry(row: {
	id: string;
	title: string;
	document?: Record<string, unknown> | null;
	inputBrief?: Record<string, unknown> | null;
	episodeSummary?: string | null;
	publishedAt: Date | string | null;
	publishTag?: unknown;
}): ChannelCatalogEntry | null {
	if (!row.publishedAt) return null;
	const tag = parseChannelPublishTag(row.publishTag);
	if (!tag) return null;

	const document = row.document ?? {};
	const inputBrief = row.inputBrief ?? {};
	const title =
		(typeof document.title === "string" && document.title.trim()) ||
		row.title.trim();
	const logline =
		typeof document.logline === "string" ? document.logline.trim() : "";
	const summary =
		row.episodeSummary?.trim() ||
		buildChannelCatalogSummary({ title, logline });

	return {
		videoProjectId: row.id,
		title,
		summary,
		durationMs: tag.durationMs,
		aspect: tag.aspect,
		format: tag.format,
		publishedAt: new Date(row.publishedAt).toISOString(),
	};
}
